// Pinia store for Systems — IT landscape entries scoped to a Stek.
import { until } from "@vueuse/core";

import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { Ref, ref, watch } from "vue";

import {
    isAbortError,
    useLatestRequest,
} from "@/composables/use-latest-request";
import { api, type types } from "@/lib/openapi";
import { useCurrentEntityManager } from "@/stores/entity-store";

export type Application = types.Application;

export const useApplicationsStore = defineStore("applications", () => {
    const toast = useToast();
    const { stek } = useCurrentEntityManager();

    const applications: Ref<Application[]> = ref([]);
    const allApplicationsWithEntities: Ref<
        Array<{ application: Application; entities: types.ApplicationEntity[] }>
    > = ref([]);
    const isLoading: Ref<boolean> = ref(false);
    // Latest-request guards: each fetch gets its own AbortController + token, so a
    // superseded run can neither abort the current one nor commit stale results.
    // fetchApplications and fetchAllApplicationsWithEntities use SEPARATE guards
    // — they run concurrently (stek watch + DevPage onMounted) and must not abort
    // each other, or the superseded one would leak isLoading and hang the spinner.
    const listRequest = useLatestRequest();
    const withEntitiesRequest = useLatestRequest();

    const ensureStekSelected = async () => {
        if (stek.value?.identity) return true;

        try {
            await until(() => stek.value?.identity).toBeTruthy({
                timeout: 10000,
            });
            return Boolean(stek.value?.identity);
        } catch {
            return false;
        }
    };

    const fetchApplications = async () => {
        // Start a new request: aborts the previous one, our own signal + token.
        const { token, signal } = listRequest.begin();

        if (!stek.value?.identity) {
            applications.value = [];
            return;
        }

        isLoading.value = true;
        try {
            const { data, response } =
                await api.startDevApplicationMgtGetApplicationsByStekStartPost({
                    signal,
                });

            // A newer request superseded us while fetching — commit nothing.
            if (!listRequest.isCurrent(token)) {
                return;
            }

            if (response.ok && data) {
                applications.value = data;
            } else if (!response.ok) {
                toast.add({
                    severity: "error",
                    summary: "Could not load applications",
                    detail: "Server returned " + response.status,
                });
            }
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            throw e;
        } finally {
            // Only the current request owns the loading flag.
            if (listRequest.isCurrent(token)) {
                isLoading.value = false;
            }
        }
    };

    watch(() => stek.value?.identity, fetchApplications, { immediate: true });

    const create = async (
        name: string,
        variant: "build" | "third_party" = "build",
        responsibleRoleId?: string
    ): Promise<Application | undefined> => {
        isLoading.value = true;
        try {
            const { data, response } =
                await api.startDevApplicationMgtCreateApplicationStartPost({
                    body: {
                        name,
                        variant,
                        responsible_role_id: responsibleRoleId ?? null,
                    },
                });
            if (response.ok && data) {
                await fetchAllApplicationsWithEntities();
                return data;
            } else {
                toast.add({
                    severity: "error",
                    summary: "Could not create application",
                    detail: "Server returned " + response.status,
                });
            }
        } finally {
            isLoading.value = false;
        }
    };

    const remove = async (applicationId: string): Promise<boolean> => {
        isLoading.value = true;
        try {
            const { response } =
                await api.startDevApplicationMgtDeleteApplicationStartPost({
                    body: { application_id: applicationId },
                });
            if (response.ok) {
                await fetchAllApplicationsWithEntities();
                return true;
            }
            toast.add({
                severity: "error",
                summary: "Could not delete application",
                detail: "Server returned " + response.status,
            });
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    // Fetches all Applications in the current Stek together with their ApplicationEntities,
    // so UIs like the cross-application relationship picker can render grouped options.
    const fetchAllApplicationsWithEntities = async () => {
        // Own guard (separate from fetchApplications) so a re-entrant call
        // supersedes only itself and never aborts the list fetch.
        const { token, signal } = withEntitiesRequest.begin();

        if (!(await ensureStekSelected())) {
            if (withEntitiesRequest.isCurrent(token))
                allApplicationsWithEntities.value = [];
            return;
        }

        try {
            // Single batch call: the server returns every application with its
            // entities in one walk, replacing the old 1 + N (one entity fetch per
            // application) that delayed the dev landscape and navigation tree.
            const { data, response } =
                await api.startDevApplicationMgtGetApplicationsByStekWithEntitiesStartPost(
                    { signal }
                );

            if (!withEntitiesRequest.isCurrent(token)) return;

            if (!response.ok || !data) {
                toast.add({
                    severity: "error",
                    summary: "Could not load applications",
                    detail: "Server returned " + response.status,
                });
                return;
            }

            allApplicationsWithEntities.value = data;
            applications.value = data.map((item) => item.application);
        } catch (e) {
            if (isAbortError(e)) return;
            throw e;
        }
    };

    return {
        applications,
        allApplicationsWithEntities,
        isLoading,
        fetchApplications,
        fetchAllApplicationsWithEntities,
        create,
        remove,
    };
});

export const useApplicationsManager = () => storeToRefs(useApplicationsStore());

/**
 * Case-insensitive name ordering for Application lists; falls back to the
 * identity when an application has no name.
 */
export const sortApplicationsByName = <
    T extends { name?: string; identity?: string },
>(
    applications: T[]
): T[] =>
    [...applications].sort((a, b) =>
        (a.name || a.identity || "")
            .toLowerCase()
            .localeCompare((b.name || b.identity || "").toLowerCase())
    );
